import { useState } from "react";
import { AlertTriangle, Loader2, Lock, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const SOURCE = {
  ai: ["AI", "bg-slate-100 text-slate-600"],
  ai_checked: ["AI + top-model check", "bg-blue-50 text-blue-700"],
  library: ["Phrase library", "bg-teal-50 text-teal-700"],
  manual: ["Your edit", "bg-emerald-50 text-emerald-700"],
};

const SourceTag = ({ seg }) => {
  if (seg.status === "pending") return <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> translating</span>;
  if (seg.status === "failed") return <span className="text-[10px] font-mono uppercase text-red-600">failed, showing English</span>;
  const [label, cls] = SOURCE[seg.source] || ["", ""];
  return label ? <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${cls}`}>{seg.source === "manual" && <Lock className="h-2.5 w-2.5" />}{label}</span> : null;
};

export const SegmentRow = ({ seg, canEdit, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(seg.es || "");
  const [busy, setBusy] = useState(false);
  const tid = seg.id;

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/segments/${seg.id}`, { es: value });
      onSaved(seg.id, r.data);
      setEditing(false);
      toast.success("Saved. Live in a few seconds.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const accept = async () => {
    try {
      await api.post(`/segments/${seg.id}/accept`);
      onSaved(seg.id, { needs_check: false });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid={`segment-row-${tid}`} className={`grid grid-cols-1 lg:grid-cols-2 border-b border-slate-100 last:border-b-0 ${seg.needs_check ? "bg-blue-50/60" : ""}`}>
      <div className="px-5 py-4 lg:border-r border-slate-100 text-sm text-slate-600 leading-relaxed">
        <div className="lg:hidden text-[10px] font-mono uppercase text-slate-400 mb-1">English</div>
        <span data-testid={`segment-en-${tid}`}>{seg.en}</span>
        {seg.needs_check && seg.prev_en && (
          <div className="mt-2 text-xs text-slate-500"><span className="font-mono uppercase text-[10px]">was:</span> <span className="line-through">{seg.prev_en}</span></div>
        )}
      </div>
      <div className="px-5 py-4 text-sm">
        {seg.needs_check && (
          <div className="flex flex-wrap items-center gap-2 mb-2 text-xs text-blue-800" data-testid={`segment-needs-check-${tid}`}>
            <AlertTriangle className="h-3.5 w-3.5" /> English changed, check Spanish
            {seg.prev_es && <span className="text-slate-500">(your edit was: “{seg.prev_es}”)</span>}
            <button className="underline disabled:opacity-50" disabled={!canEdit || seg.status === "pending"} onClick={accept} data-testid={`segment-accept-${tid}`}>Spanish is fine</button>
          </div>
        )}
        {editing ? (
          <div className="space-y-2">
            <Textarea value={value} onChange={(e) => setValue(e.target.value)} rows={Math.min(6, Math.max(2, Math.ceil(value.length / 70)))} autoFocus data-testid={`segment-edit-input-${tid}`} />
            <div className="flex gap-2">
              <Button size="sm" onClick={save} disabled={busy || !value.trim()} data-testid="save-sentence-edit-button">{busy ? "Saving…" : "Save"}</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setValue(seg.es || ""); }} data-testid="cancel-sentence-edit-button">Cancel</Button>
            </div>
          </div>
        ) : (
          <button type="button" disabled={!canEdit} onClick={() => { setValue(seg.es || ""); setEditing(true); }} data-testid={`segment-es-${tid}`}
            className="group w-full text-left text-slate-900 leading-relaxed rounded -mx-1 px-1 hover:bg-slate-50 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors duration-150">
            {seg.es || <span className="text-slate-400 italic">Not translated yet. English shows.</span>}
            {canEdit && <Pencil className="inline h-3 w-3 ml-1.5 text-slate-300 group-hover:text-slate-600" />}
          </button>
        )}
        <div className="mt-2"><SourceTag seg={seg} /></div>
      </div>
    </div>
  );
};
